import React, { useEffect, useState, Fragment, createElement, Component } from 'react';
import { ClockIcon, ShoppingCartIcon, FlameIcon, UsersIcon } from 'lucide-react';
import { useMidnightCountdown } from '../hooks/useMidnightCountdown';
import { useMetaPixel, getCtaPixelIdsForPath } from '../hooks/useMetaPixel';
import { LegalLinks } from '../components/LegalLinks';
import { RollingNumber } from '../components/RollingNumber';
import { SalesNotifications } from '../components/SalesNotifications';
// Floating WhatsApp contact — matches the /demo bubble. Left as-is per spec.
const WHATSAPP_LINK = 'https://wa.me/17869352183?text=' + encodeURIComponent('¿Cómo podemos ayudarte?');
// Scarcity meter — mirrors <ScarcityCountdown /> up top so the CTA column reads
// congruently: only 100 members at this price, with a live "spots remaining"
// bar. Same numbers (71 → 97 cap, ~1 every 3.5 min) and the same cookie start,
// so both blocks on /demo stay in sync across reloads.
const SCARCITY_TOTAL_SPOTS = 100;
const SCARCITY_COOKIE = 'scarcity_100_start';
const SCARCITY_WINDOW_MS = 24 * 60 * 60 * 1000;
const SCARCITY_START_CLAIMED = 71;
const SCARCITY_MAX_CLAIMED = 97;
function readScarcityCookie(name: string): string | null {
  const nameEQ = name + '=';
  for (let c of document.cookie.split(';')) {
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
}
function writeScarcityCookie(name: string, value: string, ms: number) {
  const date = new Date();
  date.setTime(date.getTime() + ms);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
}
export function CheckoutBundlePage() {
  const {
    hours,
    minutes,
    seconds
  } = useMidnightCountdown();
  const pad = (n: number) => n < 10 ? `0${n}` : n;
  // Live members-claimed count for the CTA scarcity bar.
  const [claimed, setClaimed] = useState(SCARCITY_START_CLAIMED);
  // When embedded inside /demo, the parent drives the count in lockstep with
  // the pop-up notifications (message `demo:claimed`). Once we receive one, we
  // stop the self-contained timer and follow the parent so both meters match.
  const [parentControlled, setParentControlled] = useState(false);
  // STANDALONE /147: the on-page sales pops drive the count the same way the
  // /demo parent does. Once the first pop fires we hand the meter over to the
  // pops (salesControlled) so the progress bar advances in lockstep with them
  // instead of the self-contained timer.
  const [salesControlled, setSalesControlled] = useState(false);
  const handleSale = () => {
    setSalesControlled(true);
    setClaimed(c => Math.min(SCARCITY_MAX_CLAIMED, c + 1));
  };
  // Arm the standalone /147 sales pops once the visitor scrolls past the hero.
  const [salesArmed, setSalesArmed] = useState(false);
  // Floating bottom "add to cart" bar + WhatsApp bubble appear on scroll.
  const [showCartBar, setShowCartBar] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onParentClaimed = (e: MessageEvent) => {
      const data = e.data;
      if (!data || data.type !== 'demo:claimed') return;
      const c = Number(data.claimed);
      if (!Number.isFinite(c)) return;
      setParentControlled(true);
      setClaimed(Math.min(SCARCITY_MAX_CLAIMED, Math.max(SCARCITY_START_CLAIMED, Math.round(c))));
    };
    window.addEventListener('message', onParentClaimed);
    return () => window.removeEventListener('message', onParentClaimed);
  }, []);
  useEffect(() => {
    // Skip the self-contained climb once the parent (in-demo) OR the on-page
    // sales pops (standalone /147) take over the count.
    if (parentControlled || salesControlled) return;
    let start = readScarcityCookie(SCARCITY_COOKIE);
    const now = Date.now();
    if (start) {
      const age = now - parseInt(start, 10);
      if (!Number.isFinite(age) || age >= SCARCITY_WINDOW_MS) start = null;
    }
    if (!start) {
      start = now.toString();
      writeScarcityCookie(SCARCITY_COOKIE, start, SCARCITY_WINDOW_MS);
    }
    const startTs = parseInt(start, 10);
    const tick = () => {
      const elapsed = Date.now() - startTs;
      const gained = Math.floor(elapsed / (3.5 * 60 * 1000));
      setClaimed(Math.min(SCARCITY_MAX_CLAIMED, SCARCITY_START_CLAIMED + gained));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [parentControlled, salesControlled]);
  const spotsLeft = Math.max(0, SCARCITY_TOTAL_SPOTS - claimed);
  const claimedPct = Math.min(100, Math.round(claimed / SCARCITY_TOTAL_SPOTS * 100));
  // Offer config per route: /147 runs the $147 offer and /97 the $97 offer
  // (both discounted, with the testimonial video); / keeps the standard $297
  // offer. Note: "147" never contains "97" and vice-versa, so the two flags are
  // mutually exclusive.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  // Embedded mode: when this page is loaded inside the /demo iframe (via
  // `/49?embed=1`), hide the sticky red offer bar so it flows into the demo
  // page instead of showing a second floating header.
  const isEmbedded = (() => {
    try {
      return new URLSearchParams(window.location.search).get('embed') === '1';
    } catch {
      return false;
    }
  })();
  // /49 is the $49/mo subscription duplicate of /97 (checked first so it never
  // collides with the other price flags).
  // /297 is the standard offer route (moved off `/`). It must NOT be mistaken
  // for the $97 offer even though the string "297" contains "97", so it is
  // detected first and excluded from the $97 flag.
  const is297 = path.includes('297');
  const is49 = path.includes('49');
  const is97 = !is49 && !is297 && path.includes('97');
  const is147 = path.includes('147');
  // Any discounted route shares the alt layout (testimonial video + alt checkout).
  const isAltOffer = is49 || is97 || is147;
  // NOTE: the /147 route now DISPLAYS $27 (the checkout link is unchanged — it
  // still points at the gQ88l Fanbasis page below). Only the shown price moved.
  const priceLabel = is49 ? '$49/mo' : is97 ? '$97' : is147 ? '$27' : '$297';
  const installmentLabel = is97 ? '$24.25' : is147 ? '$38' : '$75.75';
  // /49 uses the $49/mo subscription payment link; other routes keep their one-time links.
  const checkoutUrl = is49 ? 'https://www.fanbasis.com/agency-checkout/true-marketer/rMLgk' : is97 ? 'https://www.fanbasis.com/agency-checkout/true-marketer/EKv1l' : is147 ? 'https://www.fanbasis.com/agency-checkout/true-marketer/gQ88l' : 'https://www.fanbasis.com/agency-checkout/true-marketer/D9Vl5';
  // CTA-conversion datasets. When embedded inside the /demo iframe this page
  // must report to the HOST page's datasets (the iframe's own path is /147, so
  // its route mapping would be wrong). Read the host path from the referrer.
  const {
    trackStandardEventTo,
    initPixels
  } = useMetaPixel();
  const ctaPixelIds = (() => {
    let hostPath = path;
    if (isEmbedded && typeof document !== 'undefined' && document.referrer) {
      try {
        hostPath = new URL(document.referrer).pathname;
      } catch {
        /* keep own path */
      }
    }
    return getCtaPixelIdsForPath(hostPath);
  })();
  useEffect(() => {
    initPixels(ctaPixelIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Standard Meta product identity for the CTA conversions.
  const ctaProductData = {
    content_name: '48 Hrs AI Cash Machine',
    content_ids: ['48hrs-ai-cash-machine'],
    content_type: 'product',
    value: is49 ? 49 : is97 ? 97 : is147 ? 27 : 297,
    currency: 'USD'
  };
  const handleCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    // Fire the funnel events to every configured dataset before navigating.
    for (const pixelId of ctaPixelIds) {
      trackStandardEventTo(pixelId, 'PageView');
      trackStandardEventTo(pixelId, 'ViewContent', ctaProductData);
      trackStandardEventTo(pixelId, 'AddToCart', ctaProductData);
    }
    // When embedded inside the /demo iframe, navigating `window.location`
    // would only redirect the (framed) offer page — and Fanbasis blocks
    // framing, so the click appeared to do nothing. Break out to the top
    // window so the checkout loads in the full tab.
    if (isEmbedded) {
      try {
        ;
        (window.top ?? window).location.href = checkoutUrl;
        return;
      } catch {
        // Cross-origin access to window.top is blocked — fall back to a
        // top-level navigation via a synthesized target="_top" link.
        const a = document.createElement('a');
        a.href = checkoutUrl;
        a.target = '_top';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
    }
    window.location.href = checkoutUrl;
  };
  // STANDALONE /147 ONLY — the ported /demo social-proof + CTA treatment. None
  // of this runs when embedded in the /demo iframe or on any other route.
  const isStandalone147 = is147 && !isEmbedded;
  // Reveal the bottom floating cart bar (and lift the WhatsApp bubble) once the
  // visitor scrolls past the hero, and arm the sales pops at the same point.
  useEffect(() => {
    if (!isStandalone147) return;
    const onScroll = () => {
      const past = window.scrollY > 700;
      setShowCartBar(past);
      if (past) setSalesArmed(true);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isStandalone147]);
  // Testimonial video (Wistia) — shown only on /147. Load the Wistia player
  // scripts once when that route renders.
  const TESTIMONIAL_MEDIA_ID = '2vi3eq1778';
  useEffect(() => {
    if (!isAltOffer) return;
    const ensureScript = (src: string, asModule = false) => {
      if (document.querySelector(`script[src="${src}"]`)) return;
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      if (asModule) s.type = 'module';
      document.body.appendChild(s);
    };
    ensureScript('https://fast.wistia.com/player.js');
    ensureScript(`https://fast.wistia.com/embed/${TESTIMONIAL_MEDIA_ID}.js`, true);
  }, [isAltOffer]);
  // Embedded mode: report the full document height to the parent (the /demo
  // iframe) so it can size itself to the content and there is no inner scroll.
  useEffect(() => {
    if (!isEmbedded || typeof window === 'undefined') return;
    const postHeight = () => {
      const h = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      window.parent.postMessage({
        type: 'demo49:height',
        height: h
      }, '*');
    };
    postHeight();
    // Re-measure on resize and as async content (images/video) loads in.
    const ro = new ResizeObserver(() => postHeight());
    ro.observe(document.body);
    window.addEventListener('load', postHeight);
    const interval = window.setInterval(postHeight, 1000);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', postHeight);
      window.clearInterval(interval);
    };
  }, [isEmbedded]);
  const ImageFallback = ({
    text
  }: {
    text: string;
  }) => <div className="w-full aspect-square rounded-xl flex flex-col items-center justify-center text-center p-4 bg-gradient-to-br from-[#f0f9ff] to-[#cffafe] border border-[#67e8f9] font-['Anton'] text-[#0e7490] text-xl tracking-wide" data-id="element-230" data-mp="28z74vklqxi28">
      {text.split('\\n').map((line, i) => <Fragment key={i} data-id="element-231" data-mp="2mqvla0312sp0" data-mpi={i}>
          {line}
          <br data-id="element-232" data-mp="2ms6krnpfxeab" />
        </Fragment>)}
    </div>;
  return <div className={`min-h-screen text-[#0f172a] font-['Inter'] selection:bg-[#36D9F4]/40 ${isEmbedded ? 'bg-[#f0f2f5]' : "bg-[url('https://cdn.magicpatterns.com/uploads/n7ea57jK586HPiiMHqCkL9/private_openrouter-generated-image-1781212646060.png')] bg-cover bg-center bg-fixed"}`} data-id="element-233" data-mp="2rodt1n5b71vf">
      {/* HERO SECTION */}
      <header className={`bg-transparent border-b border-[#e2e8f0] ${isEmbedded ? 'pt-8 pb-12' : 'pb-12'}`} data-id="element-234" data-mp="w6uciak1eb27">
        <div className="max-w-[1180px] mx-auto px-5" data-id="element-235" data-mp="s7lhl2d9g752">
          {/* Dark headline banner — hidden in embedded (in-demo) mode */}
          {!isEmbedded && <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen mb-10 bg-gradient-to-b from-[#1e293b] to-[#0f172a] overflow-hidden" data-id="element-236" data-mp="igigi0kbzs5s">
              <div className="absolute inset-0 bg-[radial-gradient(80%_120%_at_50%_0%,rgba(54,217,244,0.18)_0%,transparent_60%)] pointer-events-none" data-id="element-237" data-mp="di78lvdljrq7" />
              <div className="relative max-w-[920px] mx-auto px-5 py-12 sm:py-16" data-id="element-238" data-mp="4gpwsp782c7b">
                <h1 className="relative font-['Anton'] text-[clamp(28px,4.8vw,54px)] leading-[1.02] text-center uppercase tracking-[0.5px] text-[#ffffff]" data-id="element-239" data-mp="4itzj3tdx560">
                  Genera Tu Primer Ingreso con AI en las{' '}
                  <span className="text-[#36D9F4]" data-id="element-240" data-mp="1tvnw255k9l6t">Próximas 48 Horas</span> —
                  Sin Experiencia y Sin Adivinar
                </h1>
              </div>
            </div>}

          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-10 items-start" data-id="element-241" data-mp="21dccuebbcb0n">
            {/* LEFT COLUMN */}
            <div className="relative rounded-3xl border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] bg-white px-5 sm:px-7 pb-8 mt-20 sm:mt-28" data-id="element-242" data-mp="2b4giuoj0gljc">
              <div className="-mt-28 sm:-mt-36 mb-4 flex justify-center" data-id="element-243" data-mp="2g65r5weuml3q">
                <img src="https://cdn.magicpatterns.com/uploads/gnQatCwJe9kqMyVCDBjSpQ/Whole_course.png" alt="48 Hrs AI Cash Machine — Bundle Completo" className="max-w-full lg:max-w-[620px] h-auto" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }} data-id="element-244" data-mp="1ihvpjiiz5zd5" />
                <div className="hidden w-full max-w-[480px]" data-id="element-245" data-mp="33ufamrolawrp">
                  <ImageFallback text="WHOLE BUNDLE" data-id="element-246" data-mp="33w02g9jis1lv" />
                </div>
              </div>

              <div className="flex flex-col items-center mb-6" data-id="element-247" data-mp="37x3jv0h7u5p7">
                <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[320px] h-auto" data-id="element-248" data-mp="36ev82wxgjcip" />
              </div>

              <ul className="list-none space-y-0" data-id="element-249" data-mp="3ga6k37hhe5mr">
                {[{
                title: '48 Hrs AI Cash Machine',
                accent: 'Cash Machine',
                sub: 'El sistema central de 7 pasos para generar tu primer ingreso con AI.'
              }, {
                title: 'AI Web Closer Academy',
                accent: 'Web Closer',
                sub: 'Incluye el software AI Website Sales Accelerator y los scripts de cierre.'
              }, {
                title: 'Facebook Ads Expert',
                accent: 'Facebook',
                sub: 'Mastery Course & Systems: targeting, creación, optimización y escala.'
              }, {
                title: 'Build AI Websites',
                accent: 'AI Websites',
                sub: 'AI Website Builder System + plantillas de sitios incluidas.'
              }, {
                title: 'The Ultimate AI Creator Bundle',
                accent: 'AI Creator',
                sub: 'Produce imágenes y videos con AI de nivel profesional.'
              }, {
                title: 'Custom AI Brains',
                accent: 'AI Brains',
                sub: 'Autopilot AI System: tu asistente AI preprogramado para cada paso.'
              }, {
                bonus: true,
                title: 'BONO: Llamadas en vivo todos los Lunes',
                accent: 'BONO:',
                sub: '12 meses de acompañamiento en directo, cada semana.'
              }, {
                bonus: true,
                title: 'BONO: Website Templates Con CRM Customized',
                accent: 'BONO:',
                sub: 'Plantillas de sitios web con CRM personalizado.'
              }, {
                bonus: true,
                title: 'BONO: Acceso a la comunidad privada de 48 Hrs AI Cash Machine en SKOOL',
                accent: 'BONO:',
                sub: 'Únete a la comunidad privada en la aplicación SKOOL.'
              }].map((item, i) => {
                const [before, after] = item.accent ? (() => {
                  const idx = item.title.indexOf(item.accent);
                  return idx === -1 ? [item.title, ''] : [item.title.slice(0, idx), item.title.slice(idx + item.accent.length)];
                })() : [item.title, ''];
                return <li key={i} className="relative pl-9 py-3 border-b border-[#e2e8f0]" data-id="element-250" data-mp="2agnz0p5ghu7s" data-mpi={i}>
                      <span className="absolute left-0 top-3.5 w-5 h-5 rounded-full bg-[#0350AE] flex items-center justify-center" data-id="element-251" data-mp="25hbck5ihyp5q">
                        <span className="w-3.5 h-3.5 bg-white" style={{
                      WebkitMask: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14m-6-6 6 6-6 6'/></svg>\") center/contain no-repeat",
                      mask: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14m-6-6 6 6-6 6'/></svg>\") center/contain no-repeat"
                    }} data-id="element-252" data-mp="20hyj31x4eju6" />
                      </span>
                      <p className="font-extrabold text-[16px] sm:text-[17px] leading-tight text-[#0f172a]" data-id="element-253" data-mp="xpcg5f5471nd">
                        {item.accent ? <>
                            {before}
                            <span className="text-[#dc2626]" data-id="element-254" data-mp="17pd4xglr69yy">
                              {item.accent}
                            </span>
                            {after}
                          </> : item.title}
                      </p>
                      <p className="mt-1 text-[14px] leading-snug text-[#64748b]" data-id="element-255" data-mp="39hrlab8nf6d9">
                        {item.sub}
                      </p>
                    </li>;
              })}
              </ul>

              <div className="text-center mt-7 mb-2" data-id="element-256" data-mp="1k4kvacd9coee">
                <div className="font-['Anton'] tracking-[1px] text-[26px] sm:text-[32px] text-[#0f172a] mb-4" data-id="element-257" data-mp="1f6t6vsbtnkey">
                  Precio exclusivo de hoy…
                </div>
                <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-id="element-258" data-mp="1ox49nbjrrnk9">
                  <span className="font-['Anton'] text-[56px] sm:text-[80px] leading-none text-[#0f172a] line-through decoration-[#dc2626] decoration-[5px]" data-id="element-259" data-mp="117tdy8ha0jcz">
                    $1,997
                  </span>
                  <span className="font-['Anton'] text-[52px] sm:text-[76px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-6 sm:px-8 pt-2 pb-1 rounded-xl" data-id="element-260" data-mp="1a99i3yhhzp8r">
                    {priceLabel}
                  </span>
                </div>
                <div className="font-['Anton'] tracking-[0.5px] text-[18px] sm:text-[22px] text-[#475569] mt-5" data-id="element-261" data-mp="2lprejyl3hrve">
                  {is49 ? 'Cancela cuando quieras' : is147 ? 'Pago único de hoy' : `O 4 pagos de ${installmentLabel}`}
                </div>
              </div>

              <div className="flex items-center gap-5 justify-center mt-8 max-w-[460px] mx-auto" data-id="element-262" data-mp="2u6i56vvidy3g">
                <img src="https://cdn.magicpatterns.com/uploads/we8juYLVhboSuTkVksAsiQ/money_back.png" alt="Garantía de devolución 100% — Reembolso garantizado" className="shrink-0 w-[110px] sm:w-[124px] h-auto" data-id="element-263" data-mp="2f97qzerhee41" />
                <p className="text-[15px] sm:text-[16px] text-[#0f172a] text-left leading-relaxed" data-id="element-264" data-mp="37yevqvhj3zxq">
                  Si no quedas completamente satisfecho, te ofrecemos una{' '}
                  <b className="text-[#dc2626] font-bold" data-id="element-265" data-mp="2y5ze5za59ddd">
                    garantía de devolución total
                  </b>
                  . <span className="underline" data-id="element-266" data-mp="33393zg1g3r1q">Sin preguntas.</span>
                </p>
              </div>
            </div>

            {/* RIGHT COLUMN: CTA */}
            <div className="lg:sticky lg:top-8" data-id="element-267" data-mp="3vnhc800ffmqn">
              <div className="bg-[#ffffff] border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6 sm:p-8 text-center" data-id="element-268" data-mp="1r1v0o83qo7tk">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] text-[#FD0200] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[1.5px] mb-3" data-id="element-269" data-mp="1vzefpa89gomk">
                  <FlameIcon className="w-3.5 h-3.5" data-id="element-270" data-mp="1ma4mfr1okzwv" />
                  Solo 100 miembros afortunados
                </div>
                <h3 className="text-[20px] font-[800] mb-1 text-[#0f172a]" data-id="element-271" data-mp="18ac1kccu5dwt">
                  Asegura tu acceso hoy
                </h3>
                <p className="text-[13px] text-[#64748b] mb-5" data-id="element-272" data-mp="1cadrxpd4ekws">
                  Cuando lleguemos a 100 miembros el precio vuelve a $1,997.
                </p>

                {/* Members progress — same scarcity meter as the top of /demo */}
                <div className="mb-6 text-left" data-id="element-273" data-mp="2ss04rtta5czi">
                  <div className="flex items-center justify-between text-[12px] font-semibold text-[#050505] mb-1.5" data-id="element-274" data-mp="2sp5jy16ctq5k">
                    <span className="inline-flex items-center gap-1.5" data-id="element-275" data-mp="m2gc2f6vojfh">
                      <UsersIcon className="w-3.5 h-3.5 text-[#FD0200]" data-id="element-276" data-mp="m0lqcyns8nac" />
                      <RollingNumber value={claimed} className="font-['Anton'] text-[15px] text-[#FD0200]" data-id="element-277" data-mp="h56doa1igxfi" />
                      <span className="text-[#65676b] font-medium" data-id="element-278" data-mp="vtu5c588kn06">
                        / 100 reclamados
                      </span>
                    </span>
                    <span className="text-[#FD0200] font-extrabold" data-id="element-279" data-mp="14sehenjajyff">
                      ¡Solo quedan {spotsLeft}!
                    </span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-[#f0f2f5] overflow-hidden" role="progressbar" aria-valuenow={claimed} aria-valuemin={0} aria-valuemax={SCARCITY_TOTAL_SPOTS} data-id="element-280" data-mp="1erdgbee98fp8">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#FD0200] to-[#AA0E13] transition-[width] duration-700 ease-out" style={{
                    width: `${claimedPct}%`
                  }} data-id="element-281" data-mp="19qg1nbsge3sh" />
                  </div>
                </div>

                {/* Price recap */}
                <div className="flex items-center justify-center gap-3 mb-6 flex-wrap" data-id="element-282" data-mp="54rj9acigpko">
                  <span className="font-['Anton'] text-[28px] text-[#64748b] line-through decoration-[#94a3b8]" data-id="element-283" data-mp="9631gut228iu">
                    $1,997
                  </span>
                  <span className="font-['Anton'] text-[40px] bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-5 pt-1 rounded-md" data-id="element-284" data-mp="j37sp91rcu6i">
                    {priceLabel}
                  </span>
                </div>

                <a href="#" onClick={handleCheckout} className="relative overflow-hidden flex items-center justify-center gap-2.5 w-full bg-[#3FAC00] hover:bg-[#43B800] text-[#ffffff] font-extrabold text-[20px] tracking-tight p-4 rounded-lg transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.07)] active:shadow-[0_2px_0_#2C7A00,0_1px_2px_rgba(0,0,0,0.07)]" data-id="element-285" data-mp="s11mac8519pz">
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-white/35 to-transparent" data-id="element-286" data-mp="2w8w9z32otltw" />
                  <ShoppingCartIcon className="relative w-5 h-5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-287" data-mp="2r907575q80fx" />
                  <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-288" data-mp="2mb8c449sby7i">
                    Obtén Acceso Ahora
                  </span>
                </a>

                <img src="https://cdn.magicpatterns.com/uploads/76cD3Rx79bU816uiFSdXqq/6814f798ca2000ae092e5f68.png" alt="Pago seguro con Stripe — Visa, Mastercard, American Express, Discover, Payoneer, Apple Pay, PayPal" className="w-full max-w-[220px] h-auto mx-auto mt-4" data-id="element-289" data-mp="3eznftunguu3j" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* QUOTE SECTION */}
      <section className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] text-[#ffffff] text-center py-6 px-5" data-id="element-290" data-mp="1l0vyg7kb801">
        <p className="max-w-[880px] mx-auto text-[clamp(16px,2.2vw,22px)] font-light leading-[1.35] text-[#ffffff]" data-id="element-291" data-mp="3sywtamkafa3t">
          "No necesitas más motivación. Necesitas un sistema que te dé
          resultados esta semana — y que dependa de tu ejecución, no de tu
          suerte." <b className="font-bold text-[#ffffff]" data-id="element-292" data-mp="33izcmf4219go">— Gabriel Beltrán</b>
        </p>
      </section>

      {/* TESTIMONIAL VIDEO — only on /147; hidden when embedded in the demo,
                                         which already shows the testimonial under "Resultados Reales". */}
      {isAltOffer && !isEmbedded && <section className="py-12 px-5 bg-transparent" data-id="element-293" data-mp="51vctppvvcea">
          <div className="max-w-[820px] mx-auto text-center" data-id="element-294" data-mp="53et3gbjhng2">
            <span className="inline-block bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold text-[clamp(13px,2vw,18px)] leading-tight py-2.5 px-6 rounded-full mb-6" data-id="element-295" data-mp="3mgsf5garnhdj">
              Lo que dicen nuestros estudiantes
            </span>
            <div className="relative w-full overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] bg-black" style={{
          paddingTop: '56.25%'
        }} dangerouslySetInnerHTML={{
          __html: "<style>{`wistia-player[media-id='" + TESTIMONIAL_MEDIA_ID + "']:not(:defined){background:center / contain no-repeat url('https://fast.wistia.com/embed/medias/" + TESTIMONIAL_MEDIA_ID + '/swatch\');display:block;filter:blur(5px);padding-top:56.25%;}`}</style><wistia-player media-id="' + TESTIMONIAL_MEDIA_ID + '" aspect="1.7777777777777777" style="position:absolute;inset:0;width:100%;height:100%;"></wistia-player>'
        }} data-id="element-296" data-mp="nrju4g0taub6" />
          </div>
        </section>}

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-14" data-id="element-297" data-mp="1ykb1fmx0hqri">
        <div className="max-w-[1180px] mx-auto px-5" data-id="element-298" data-mp="1yldspvy8xv7y">
          <span className="block text-center bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold tracking-normal text-[clamp(14px,2.3vw,21px)] leading-tight max-w-[660px] mx-auto mb-2 py-3 px-7 rounded-[10px]" data-id="element-299" data-mp="1uhxx92xwy0wu">
            Esto es todo lo que recibes cuando aseguras tu
          </span>
          <div className="flex justify-center mt-3.5 mb-10" data-id="element-300" data-mp="22lno4yyjcx6z">
            <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[550px] h-auto" data-id="element-301" data-mp="27ljomo80y6hs" />
          </div>

          <div className="space-y-5" data-id="element-302" data-mp="2gmzzsy6nycn2">
            {[{
            title: '48 Hrs AI Cash Machine',
            tag: '(Sistema Central)',
            benefit: 'Tu primer ingreso con AI en un plan de 48 horas',
            img: 'https://cdn.magicpatterns.com/uploads/et7g9SXQ6R2S4NhvuLF2px/48_HRS.png',
            fallback: '48 HOURS\\nAI CASH MACHINE',
            bullets: ['El proceso completo de 7 pasos: Targets, Marketing, Leads, Ventas y Escala — ya armado.', 'Plantillas y prompts listos para copiar y ejecutar el mismo día.', 'Pasos concretos y medibles, sin relleno y sin teoría que no aplicas.']
          }, {
            title: 'AI Web Closer Academy',
            tag: '',
            benefit: 'Cierra clientes de negocios locales con AI',
            img: 'https://cdn.magicpatterns.com/uploads/vAtF5NAms3ZTbyUfkAw9K7/Ai_Web_Closer-2.png',
            fallback: 'AI WEB CLOSER\\nACADEMY',
            bullets: ['AI Prospecting Playbook + 90 Day AI Closing Blueprint paso a paso.', 'Acceso al software AI Website Sales Accelerator para gestionar tus cierres.', 'Local Business AI Scripts + AI Closer Elite Mastermind con la comunidad.']
          }, {
            title: 'Facebook Ads Expert',
            tag: '(Mastery Course & Systems)',
            benefit: 'Convierte la atención en clientes que pagan',
            img: 'https://cdn.magicpatterns.com/uploads/h2AGtNXx3iPYeMyn42Getp/Facebook_Ads_Expert-2.png',
            fallback: 'FACEBOOK\\nADS EXPERT',
            bullets: ['Targeting, creación de anuncios y lanzamiento — explicado paso a paso.', 'Estructura de campaña y de presupuesto que ya funciona en el mercado.', 'Optimización y escala basadas en datos reales, no en suposiciones.']
          }, {
            title: 'Build AI Websites',
            tag: '(AI Website Builder System)',
            benefit: 'Crea sitios web con AI en minutos, no en semanas',
            img: 'https://cdn.magicpatterns.com/uploads/uSEdrUzRP8WQMiL2k1kAgn/How_To_Websites.png',
            fallback: 'BUILD AI\\nWEBSITES',
            bullets: ['Sistema completo para construir sitios web profesionales usando AI.', 'Plantillas de sitios incluidas y listas para personalizar.', 'Un servicio que puedes vender a negocios locales desde el día uno.']
          }, {
            title: 'The Ultimate AI Creator Bundle',
            tag: '(Imágenes y Videos)',
            benefit: 'Produce imágenes y videos con AI de nivel profesional',
            img: 'https://cdn.magicpatterns.com/uploads/xdUczHdFzD4ia5JY8o9dbh/Ai_Image___video.png',
            fallback: 'THE ULTIMATE\\nAI CREATOR BUNDLE',
            bullets: ['AI Image Generation Mastery + Isolated AI Video Production.', "Prompt Engineering Guide y AI Creator's Workbook para producir rápido.", 'Automation & Workflow Secrets para crear contenido en serie.']
          }, {
            title: 'Custom AI Brains',
            tag: '(Autopilot AI System)',
            benefit: 'Tu asistente AI preprogramado para cada paso',
            img: 'https://cdn.magicpatterns.com/uploads/jiLDx9YCn8BDUyNwcdufrn/Custom_Lloyds-2.png',
            fallback: 'CUSTOM AI BRAINS\\nAUTOPILOT SYSTEM',
            bullets: ['AI a la medida que maneja automatización, datos y ejecución por ti.', 'Reportes y optimización sin que tengas que estar encima del proceso.', 'Diseñado para que el sistema trabaje mientras tú te enfocas en vender.']
          }].map((card, i) => <article key={i} className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-7 items-center bg-[#ffffff] border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6" data-id="element-303" data-mp="mamjuxqbbgma" data-mpi={i}>
                <div className="flex items-center justify-center" data-id="element-304" data-mp="w8sjf8oncanz">
                  <img src={card.img.startsWith('http') ? card.img : `/${card.img}`} alt={card.title} className="w-full max-h-[340px] sm:max-h-[300px] md:max-h-[230px] object-contain" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }} data-id="element-305" data-mp="1xm7hwecamhzw" />
                  <div className="hidden w-full max-w-[300px]" data-id="element-306" data-mp="1jm516vfq1um8">
                    <ImageFallback text={card.fallback} data-id="element-307" data-mp="1env6sn1tmy1o" />
                  </div>
                </div>
                <div className="text-center md:text-left" data-id="element-308" data-mp="19wlhxoo3jyqu">
                  <h2 className="text-[23px] font-[800] mb-2.5 text-[#0f172a]" data-id="element-309" data-mp="19v221z6crppl">
                    {card.title}{' '}
                    {card.tag && <span className="text-[#dc2626]" data-id="element-310" data-mp="zz7d6mbjcyed">{card.tag}</span>}
                  </h2>
                  <span className="block w-full text-center md:text-left bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold text-[15px] leading-tight px-7 py-3 rounded-full mb-3.5 shadow-sm" data-id="element-311" data-mp="vxlvmno23vxu">
                    {card.benefit}
                  </span>
                  <ul className="space-y-1.5 text-left" data-id="element-312" data-mp="37k77wa056w40">
                    {card.bullets.map((bullet, j) => <li key={j} className="relative pl-9 py-1 text-[14.5px] text-[#334155] leading-relaxed" data-id="element-313" data-mp="3bm15g9f1jjzy" data-mpi={j}>
                        <span className="absolute left-0 top-1 w-5 h-5 rounded-full bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] text-[12px] font-[800] flex items-center justify-center leading-none" data-id="element-314" data-mp="3gkvn3819v2ih">
                          →
                        </span>
                        {bullet}
                      </li>)}
                  </ul>
                </div>
              </article>)}

            {/* BONUS CARDS */}
            {[{
            num: 1,
            title: 'Llamadas en Vivo Todos los Miércoles',
            benefit: 'Acompañamiento directo durante 12 meses',
            img: 'https://cdn.magicpatterns.com/uploads/ofaWhTtk4f8R4qHM8Vt98e/Weekly_Mastermind_Calls.png',
            fallback: 'BONO\\nLLAMADAS\\nEN VIVO',
            bullets: ['Sesión semanal en vivo para resolver tus dudas reales de ejecución.', 'Revisión de tus anuncios, tu oferta y tus cierres en tiempo real.', 'Acceso a la comunidad de quienes ya están ejecutando el sistema.']
          }, {
            num: 2,
            title: 'Website Templates Con CRM Customized',
            benefit: 'Plantillas de sitios web con CRM personalizado',
            img: 'https://cdn.magicpatterns.com/uploads/6d9LM81uEV7CZ3u3aGjF2e/Website_templates.png',
            fallback: 'BONO\\nWEBSITE TEMPLATES\\n+ CRM',
            bullets: ['Plantillas de sitios web profesionales listas para personalizar y publicar.', 'CRM personalizado integrado para capturar, organizar y dar seguimiento a tus leads.', 'Todo conectado en un solo lugar para que empieces a operar esta misma semana.']
          }, {
            num: 3,
            title: 'Comunidad Privada en SKOOL',
            benefit: 'Acceso a la comunidad privada de 48 Hrs AI Cash Machine',
            img: 'https://cdn.magicpatterns.com/uploads/pKwPbw4NUGCr5KcGEsv6kD/private_openrouter-generated-image-1781213810744.png',
            fallback: 'BONO\\nCOMUNIDAD\\nPRIVADA SKOOL',
            bullets: ['Acceso a la comunidad privada de 48 Hrs AI Cash Machine en la aplicación SKOOL.', 'Conecta con otros miembros que están ejecutando el mismo sistema.', 'Comparte avances, resuelve dudas y mantente acompañado en cada paso.']
          }].map((bonus, i) => <article key={`bonus-${i}`} className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-7 items-center bg-white border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6" data-id="element-315" data-mp="14zpwcuhgmcrv" data-mpi={i}>
                <div className="flex items-center justify-center" data-id="element-316" data-mp="v9wjlq5kzrm1">
                  {bonus.img ? <img src={bonus.img} alt={bonus.title} className="w-full max-h-[340px] sm:max-h-[300px] md:max-h-[230px] object-contain" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }} data-id="element-317" data-mp="1izqyed9gnq8x" /> : null}
                  <div className={`w-full max-w-[300px] ${bonus.img ? 'hidden' : ''}`} data-id="element-318" data-mp="3r7xfmeb2et6s">
                    <ImageFallback text={bonus.fallback} data-id="element-319" data-mp="3m940khg4qu4i" />
                  </div>
                </div>
                <div className="text-center md:text-left" data-id="element-320" data-mp="3haj0qj4sicgl">
                  <h2 className="text-[23px] font-[800] mb-2.5 text-[#0f172a] leading-tight" data-id="element-321" data-mp="3hc3s5zvt5fa8">
                    <span className="text-[#dc2626]" data-id="element-322" data-mp="iru7k7g1j2hx">BONO #{bonus.num}:</span>{' '}
                    {bonus.title}
                  </h2>
                  <span className="block w-full text-center md:text-left bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold text-[15px] leading-tight px-7 py-3 rounded-full mb-3.5 shadow-sm" data-id="element-323" data-mp="92kfgrl75jtt">
                    {bonus.benefit}
                  </span>
                  <ul className="space-y-1.5 text-left" data-id="element-324" data-mp="wser55ts52ch">
                    {bonus.bullets.map((bullet, j) => <li key={j} className="relative pl-9 py-1 text-[14.5px] text-[#334155] leading-relaxed" data-id="element-325" data-mp="wqlglfg1pxz7" data-mpi={j}>
                        <span className="absolute left-0 top-1 w-5 h-5 rounded-full bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] text-[12px] font-[800] flex items-center justify-center leading-none" data-id="element-326" data-mp="192nym7n55p5m">
                          →
                        </span>
                        {bullet}
                      </li>)}
                  </ul>
                </div>
              </article>)}
          </div>

          <hr className="border-0 border-t border-[#64748b] mt-12" data-id="element-327" data-mp="1s105tnuhsoyk" />
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-14 text-center border-t border-[#e2e8f0] bg-transparent" data-id="element-328" data-mp="1wtjimilcvg2b">
        <div className="max-w-[1180px] mx-auto px-5" data-id="element-329" data-mp="21q9xx3pc67mo">
          <div className="flex justify-center mb-0 -mt-[52px]" data-id="element-330" data-mp="21t60x6g1jmhz">
            <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[499px] h-auto" data-id="element-331" data-mp="26rzeeyvbvdi5" />
          </div>

          <div className="mb-0 sm:-mb-8 flex justify-center" data-id="element-332" data-mp="bl5d7orjmgor">
            <img src="https://cdn.magicpatterns.com/uploads/gnQatCwJe9kqMyVCDBjSpQ/Whole_course.png" alt="48 Hrs AI Cash Machine — Bundle Completo" className="max-w-full sm:max-w-[1008px] h-auto" onError={e => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} data-id="element-333" data-mp="6p7uxcwpz09c" />
            <div className="hidden w-full max-w-[560px]" data-id="element-334" data-mp="pemwr9k8btes">
              <ImageFallback text="WHOLE BUNDLE" data-id="element-335" data-mp="pe4rjnfla4uq" />
            </div>
          </div>

          <div className="text-center mt-0 mb-2" data-id="element-336" data-mp="13de8wqfj750a">
            <div className="font-['Poppins'] font-normal tracking-normal text-[22px] sm:text-[28px] text-[#0f172a] mb-4" data-id="element-337" data-mp="13c33ynacnr7l">
              Precio exclusivo de hoy…
            </div>
            <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-id="element-338" data-mp="3562e35bqk2ip">
              <span className="font-['Anton'] text-[56px] sm:text-[80px] leading-none text-[#0f172a] line-through decoration-[#dc2626] decoration-[5px]" data-id="element-339" data-mp="315ryd0y55xqn">
                $1,997
              </span>
              <span className="font-['Anton'] text-[52px] sm:text-[76px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-6 sm:px-8 pt-2 pb-1 rounded-xl" data-id="element-340" data-mp="2v1cakv0ooqcd">
                {priceLabel}
              </span>
            </div>
          </div>

          <a href="#" onClick={handleCheckout} className="relative overflow-hidden flex items-center justify-center gap-3 w-full max-w-[620px] mx-auto mt-5 bg-[#3FAC00] hover:bg-[#43B800] text-[#ffffff] font-extrabold text-[18px] sm:text-[24px] tracking-tight p-4 rounded-full transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.07)] active:shadow-[0_2px_0_#2C7A00,0_1px_2px_rgba(0,0,0,0.07)]" data-id="element-341" data-mp="27c00zu1g2g9j">
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/35 to-transparent" data-id="element-342" data-mp="1jb5aw4v9h3p" />
            <ShoppingCartIcon className="relative w-6 h-6 shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-343" data-mp="1a2p3zzdevoyq" />
            <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-344" data-mp="155ymr8pvenbq">
              ¡Quiero el 48 Hrs AI Cash Machine!
            </span>
          </a>

          <div className="flex items-center gap-5 justify-center mt-8 max-w-[560px] mx-auto bg-white border-2 border-dotted border-[#D4AF37] rounded-2xl p-5 sm:p-7" data-id="element-345" data-mp="wcverfxzv2oh">
            <img src="https://cdn.magicpatterns.com/uploads/we8juYLVhboSuTkVksAsiQ/money_back.png" alt="Garantía de devolución 100% — Reembolso garantizado" className="shrink-0 w-[110px] sm:w-[124px] h-auto" data-id="element-346" data-mp="rb63q0arup0e" />
            <p className="text-[15px] sm:text-[16px] text-[#0f172a] text-left leading-relaxed" data-id="element-347" data-mp="ddhfqjler1r4">
              Si no quedas completamente satisfecho, te ofrecemos una{' '}
              <b className="text-[#dc2626] font-bold" data-id="element-348" data-mp="2bsaziuz6zi3b">
                garantía de devolución total
              </b>
              . <span className="underline" data-id="element-349" data-mp="27po4ymb4zj2f">Sin preguntas.</span>
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER — hidden in embedded (in-demo) mode; the demo has its own footer */}
      {!isEmbedded && <footer className="bg-[#0f172a] border-t border-[#1e293b] py-8 text-[12px] text-[#94a3b8]" data-id="element-350" data-mp="39suaxpshcc0">
          <div className="max-w-[1180px] mx-auto px-5" data-id="element-351" data-mp="7bnx66ndbxrw">
            <p className="max-w-[920px] mx-auto text-center leading-[1.6] text-[11px] text-[#94a3b8]" data-id="element-352" data-mp="7acxoj0yhc6l">
              En True Marketer LLC creemos que el éxito es resultado del
              trabajo, la educación y la persistencia. Los ejemplos mostrados no
              deben considerarse típicos y no hay garantía de resultados. La
              información de este sitio es educativa por naturaleza y no
              constituye asesoría legal ni financiera. Al usar este sitio
              aceptas la responsabilidad total de tus propios resultados;
              nuestro equipo está aquí para apoyarte, pero siempre debes hacer
              tu propia investigación antes de tomar cualquier decisión de
              inversión o de negocio. Este sitio no está afiliado a Meta,
              Facebook ni a ninguna plataforma de terceros.
              <br data-id="element-353" data-mp="16cn8jae03n6w" />
              <br data-id="element-354" data-mp="16c3ptrgj0urt" />
              <b className="text-[#e2e8f0]" data-id="element-355" data-mp="16blo4fb3hoci">True Marketer LLC</b>
              <br data-id="element-356" data-mp="1bci07g5logt0" />© {new Date().getFullYear()} True Marketer LLC. Todos los
              derechos reservados.
            </p>

            <LegalLinks containerClassName="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1.5 mt-5 text-[#cbd5e1]" linkClassName="hover:text-[#36D9F4] mx-1.5 underline-offset-2 hover:underline" data-id="element-357" data-mp="1gatb7u7msxct" />
          </div>
        </footer>}

      {/* STANDALONE /147 — ported /demo social-proof + CTA treatment. Guarded so
                                       it never renders when embedded in /demo or on any other route. */}
      {isStandalone147 && <>
          {/* SALES SOCIAL-PROOF POPS — armed once the visitor scrolls past the
                                               hero; each pop bumps the scarcity meter via onShow (lockstep). */}
          <SalesNotifications enabled armed={salesArmed} position="above-cta" ctaVisible={showCartBar} onShow={handleSale} data-id="element-358" data-mp="2960tf56ol4li" />

          {/* FLOATING ADD-TO-CART BAR — the entire bar is the button; rides the
                                               bottom of the page and appears once the visitor scrolls into the
                                               offer. Premium glossy red plastic look (matches /demo). */}
          <a href="#" onClick={handleCheckout} aria-label="Haz clic para obtener acceso — solo $27" aria-hidden={!showCartBar} style={{
        backgroundImage: 'linear-gradient(180deg, #FD0200 0%, #AA0E13 100%)',
        boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -4px 10px rgba(120,6,4,0.55), 0 -6px 24px rgba(0,0,0,0.18)'
      }} className={`group fixed bottom-0 inset-x-0 z-[70] isolate flex w-full items-center justify-center gap-2.5 overflow-hidden px-5 py-6 text-white transition-all duration-300 ease-out hover:brightness-[1.04] active:brightness-[0.97] ${showCartBar ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-359" data-mp="2rxa6fofnubhu">
            <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[46%] bg-gradient-to-b from-white/55 to-transparent" data-id="element-360" data-mp="1dyqhcjnogjoy" />
            <span aria-hidden="true" className="cta-sheen pointer-events-none absolute -inset-y-8 left-0 w-[26%] bg-gradient-to-r from-transparent via-white/45 to-transparent blur-[2px]" data-id="element-361" data-mp="1462mvvk66rte" />
            <ShoppingCartIcon className="relative z-10 w-5 h-5 shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-362" data-mp="v6pbgxchjt61" />
            <span className="relative z-10 flex flex-col items-center leading-tight text-center" data-id="element-363" data-mp="g9opzbkw0ug0">
              <span className="font-extrabold text-[18px] sm:text-[20px] tracking-tight [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-364" data-mp="l76w6u7isvkb">
                HAZ CLIC PARA OBTENER ACCESO
              </span>
              <span className="inline-flex items-center gap-2 text-[14px] font-normal text-white/90 [text-shadow:0_1px_1px_rgba(0,0,0,0.3)]" data-id="element-365" data-mp="2fm9fub9w1jfb">
                solo $27
                <span aria-hidden="true" className="text-white/50" data-id="element-366" data-mp="3v4z01tg7r14a">
                  |
                </span>
                <span className="font-semibold" data-id="element-367" data-mp="do7dkqrvv820">¡Solo quedan {spotsLeft}!</span>
              </span>
            </span>
          </a>

          {/* Floating WhatsApp CTA — bottom-right; lifts above the cart bar when
                                               it's visible and slides back down when it's not. Link left
                                               as-is per spec. */}
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" aria-label="Continúa la conversación por WhatsApp" className={`fixed right-6 z-[80] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-900/30 transition-all duration-300 hover:scale-105 hover:bg-[#20bd5a] focus:outline-none focus:ring-4 focus:ring-emerald-300 ${showCartBar ? 'bottom-[112px]' : 'bottom-6'}`} data-id="element-368" data-mp="wnbf7eh3hzjn">
            <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0 fill-white" aria-hidden="true" data-id="element-369" data-mp="2uvz8ng2yfgdu">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-370" data-mp="2zuk7bb2k5s06" />
            </svg>
          </a>
        </>}
    </div>;
}